'use client';

import type { BioContext } from '@ps/functions/bio/bioContext';

import { usePostDetailModal } from '../post-detail/modal/usePostDetailModal';
import { usePostUnlockActions } from '../post-view/hooks/usePostUnlockActions';
import { type PostCardData, PostChatMessageView } from './PostChatMessageView';

interface Props {
  /** The card display model (message snapshot, or the fetched post). */
  post: PostCardData;
  /** Store (creator) id the post belongs to — passed to the detail modal. */
  storeId: string;
  /** Post id — the detail modal fetches the full post by it (shared cache). */
  postId: string;
  /** Creator url code — the author fallback + pay/tip checkout scope. */
  urlCode: string;
  /**
   * The hosting surface's bio context, or `null` on the non-1:1 DM inbox (mixed
   * creators, no single scope). Threaded to the unlock flow (subscribe route vs
   * in-place modal) and the detail modal (permalink / share).
   */
  bioContext: BioContext | null;
  /**
   * Where checkout returns on a successful unlock. Pass the post's permalink so
   * "Done" lands on the (now unlocked) post — mirrors the full post view. Omit
   * to dismiss back to the launching surface with the gate lifted in place.
   */
  redirectTo?: string;
  isOwn?: boolean;
  fullWidth?: boolean;
  titleLineClamp?: number;
  descriptionLineClamp?: number;
}

/**
 * Interactive host for the shared {@link PostChatMessageView} in a chat bubble
 * (DM + community): a click on the card BODY (anywhere but the paywall CTAs)
 * opens the shared post-detail modal via {@link usePostDetailModal}, while the
 * paywall CTAs launch the subscribe / pay / tip flow DIRECTLY (no modal detour)
 * via {@link usePostUnlockActions}. On success the checkout notification
 * refreshes this card's post (`useFetchPostById`), lifting the gate in place.
 * The modal is mounted ONCE per surface by `PostDetailModalProvider`, so this
 * renders no modal of its own — keeping the pure card unaffected by recycling.
 */
export function PostChatCard({
  post,
  storeId,
  postId,
  urlCode,
  bioContext,
  redirectTo,
  isOwn,
  fullWidth,
  titleLineClamp,
  descriptionLineClamp,
}: Props) {
  const { openPostDetail } = usePostDetailModal();
  const { onSubscribe, onPay, onTip } = usePostUnlockActions({
    postId,
    urlCode,
    sellerId: storeId,
    bioContext,
    redirectTo,
  });

  return (
    <PostChatMessageView
      post={post}
      isOwn={isOwn}
      fullWidth={fullWidth}
      titleLineClamp={titleLineClamp}
      descriptionLineClamp={descriptionLineClamp}
      onOpen={() => openPostDetail({ storeId, postId, urlCode, bioContext })}
      onSubscribe={onSubscribe}
      onPay={onPay}
      onTip={onTip}
    />
  );
}
